import type { MergeFieldOverrides } from '@/services/deduplication'
import type { CandidatePatient } from '@/types/deduplication'
import { ADDRESS_COLUMNS, isBlank, resolveAddressColumns, type EffectiveField } from './merge-fields'

/**
 * Builds the merge request body: the agent's choices, the medical-history union, and the two
 * safety carries below. Anything the API can work out on its own is left out — the fewer fields
 * we send, the fewer chances to write a stale value from the duplicates list.
 */

/** MySQL DATETIME as returned by the duplicates list ⇒ the ISO format PHP validates. */
function toIsoDateTime(value: string): string {
  return value.includes('T') ? value : `${value.replace(' ', 'T')}+00:00`
}

const ISO_DATE_TIME_FIELDS = new Set(['summary_care_record_updated_at'])

export function buildMergeOverrides(
  fields: EffectiveField[],
  patients: CandidatePatient[]
): MergeFieldOverrides | undefined {
  const byHash = new Map(patients.map(patient => [patient.hash, patient]))
  const body: Record<string, string | boolean> = {}

  const put = (key: string, value: string | null | undefined) => {
    // A blank is a destructive write on the API side, and clearing a field is not a merge outcome.
    if (isBlank(value)) return
    const trimmed = (value as string).trim()
    body[key] = ISO_DATE_TIME_FIELDS.has(key) ? toIsoDateTime(trimmed) : trimmed
  }

  for (const field of fields) {
    const { key, policy } = field.field

    // The API OR's these itself but only promotes a stored '0', so a survivor with no
    // `patients_support` row loses the flag (INT-55). Drop this once the API is fixed.
    if (policy === 'flag') {
      if (field.effectiveValue === 'Yes') body[key] = true
      continue
    }

    if (policy === 'paired') continue // second pass, once we know whether the partner is sent

    if (key === 'address') {
      if (!field.overridden || !field.effectiveSource) continue
      const chosen = byHash.get(field.effectiveSource)
      if (!chosen) continue
      const columns = resolveAddressColumns(
        chosen,
        patients.find(patient => patient.hash !== field.effectiveSource)
      )
      for (const column of ADDRESS_COLUMNS) put(column, columns[column])
      continue
    }

    if (field.overridden || field.state === 'combined') {
      put(key, field.effectiveValue)
    }
  }

  // The SCR and its date must come from the same record. Sent when the partner is sent, and also
  // when the partner comes from one record while both records carry a different date: the API
  // decides the date on its own there, either by conflicting on it
  // (`handle_summary_care_record_updated_at_field` bails once the SCRs differ) or by taking the
  // later of the two — which can leave one record's SCR next to the other's date.
  //
  // Not when both records hold the same SCR: the date is then the API's alone to pick, and its
  // latest-wins is a better answer than pinning the survivor's, possibly older, timestamp.
  for (const field of fields) {
    const partnerKey = field.field.pairedWith
    if (field.field.policy !== 'paired' || !partnerKey) continue

    const partner = fields.find(candidate => candidate.field.key === partnerKey)
    const present = field.values.filter((value): value is string => !isBlank(value))
    const contested = partner?.state !== 'same' && present.length > 1 && new Set(present).size > 1

    if (partnerKey in body || contested) put(field.field.key, field.effectiveValue)
  }

  return Object.keys(body).length > 0 ? (body as MergeFieldOverrides) : undefined
}

/**
 * The API reports unmergeable fields only as a sentence — "…conflicting values: a, b" — so parse
 * defensively and fall back to the raw message. Replace once it returns `data.conflicts`.
 */
export function parseConflictFields(message: string | undefined): string[] {
  if (!message?.includes('conflicting values:')) return []
  return message
    .split('conflicting values:')[1]
    .split(',')
    .map(part => part.trim().replace(/\.$/, ''))
    .filter(Boolean)
}
